import { type DepartmentAggregates } from '../search/types';

import { type GetGroupsByIdResponse } from './aliases';
import {
  MAX_POPULAR_CATEGORIES,
  RESTRICTED_POPULAR_CATEGORY_NAMES,
} from './constants';
import { type PopularCategory } from './types';

function buildPopularCategoriesForDepartment({
  groupsById,
  departmentAggregates,
}: {
  groupsById: GetGroupsByIdResponse;
  departmentAggregates: DepartmentAggregates;
}) {
  const popularCategories = [];
  const groupAggregates = departmentAggregates.groups || [];

  for (const id in groupAggregates) {
    const group = groupsById[id];
    const count = groupAggregates[id]?.count || 0;
    const name = group?.name_i18n?.['en-US'] ?? '';

    if (
      !group ||
      count === 0 ||
      RESTRICTED_POPULAR_CATEGORY_NAMES.includes(name)
    ) {
      continue;
    }

    popularCategories.push({
      name,
      count,
      id: group.id,
      slug: group.id,
    });
  }

  return popularCategories;
}

function buildPopularCategoriesForGroup({
  groupId,
  groupsById,
  departmentAggregates,
}: {
  groupId: string;
  groupsById: GetGroupsByIdResponse;
  departmentAggregates: DepartmentAggregates;
}) {
  const popularCategories = [];
  const productTypeAggregates = departmentAggregates.product_types || [];
  const group = groupsById[groupId];

  if (!group) {
    return [];
  }

  for (const productTypeId in productTypeAggregates) {
    const count = productTypeAggregates[productTypeId]?.count || 0;

    if (count === 0) {
      continue;
    }

    const productType = group.product_types.find(
      (attribute) => attribute.id === productTypeId
    );

    if (!productType) {
      continue;
    }

    popularCategories.push({
      count,
      name: productType.name_i18n['en-US'] ?? '',
      id: productType.id,
      slug: productType.id,
      parentId: groupId,
    });
  }

  return popularCategories;
}

export function buildPopularCategories({
  groupId,
  groupsById,
  departmentAggregates,
}: {
  groupId?: string;
  groupsById: GetGroupsByIdResponse;
  departmentAggregates: DepartmentAggregates;
}) {
  let subcategoryAggregates: PopularCategory[] = [];

  if (groupId) {
    subcategoryAggregates = buildPopularCategoriesForGroup({
      groupId,
      groupsById,
      departmentAggregates,
    });
  } else {
    subcategoryAggregates = buildPopularCategoriesForDepartment({
      groupsById,
      departmentAggregates,
    });
  }

  return subcategoryAggregates
    .sort((a, b) => {
      return a.count > b.count ? -1 : 1;
    })
    .slice(0, MAX_POPULAR_CATEGORIES);
}
